/**
 * 物流管理模块 - 常量定义
 * 
 * @module logistics
 * @version 1.0.0
 * @since 2026-01-23
 */

import {
  LogisticsProviderType,
  LogisticsProviderStatus,
  LogisticsCompanyType,
  SettlementType,
  LogisticsOrderStatus,
  LogisticsTrackSource,
  LogisticsSettlementStatus,
  SettlementItemMatchStatus,
} from './enums'

/**
 * 物流供应商类型标签
 */
export const LOGISTICS_PROVIDER_TYPE_LABELS: Record<LogisticsProviderType, string> = {
  [LogisticsProviderType.EXPRESS]: '快递',
  [LogisticsProviderType.FULL_TRUCK]: '整车物流',
  [LogisticsProviderType.LTL]: '零担物流',
  [LogisticsProviderType.COLD_CHAIN]: '冷链物流',
  [LogisticsProviderType.SPECIAL_LINE]: '专线物流',
}

/**
 * 企业类型标签（物流供应商）
 */
export const LOGISTICS_COMPANY_TYPE_LABELS: Record<LogisticsCompanyType, string> = {
  [LogisticsCompanyType.STATE_OWNED]: '国企',
  [LogisticsCompanyType.PRIVATE]: '民企',
  [LogisticsCompanyType.FOREIGN]: '外企',
  [LogisticsCompanyType.INDIVIDUAL]: '个体',
}

/**
 * 结算方式标签
 */
export const SETTLEMENT_TYPE_LABELS: Record<SettlementType, string> = {
  [SettlementType.CASH]: '现结',
  [SettlementType.MONTHLY]: '月结',
  [SettlementType.PREPAID]: '预付',
}

/**
 * 物流供应商状态标签
 */
export const LOGISTICS_PROVIDER_STATUS_LABELS: Record<LogisticsProviderStatus, string> = {
  [LogisticsProviderStatus.DISABLED]: '停用',
  [LogisticsProviderStatus.ENABLED]: '启用',
}

/**
 * 物流订单状态标签
 */
export const LOGISTICS_ORDER_STATUS_LABELS: Record<LogisticsOrderStatus, string> = {
  [LogisticsOrderStatus.PENDING]: '待揽收',
  [LogisticsOrderStatus.IN_TRANSIT]: '运输中',
  [LogisticsOrderStatus.SIGNED]: '已签收',
  [LogisticsOrderStatus.CANCELLED]: '已取消',
  [LogisticsOrderStatus.EXCEPTION]: '异常',
}

/**
 * 物流订单状态颜色
 */
export const LOGISTICS_ORDER_STATUS_COLORS: Record<LogisticsOrderStatus, string> = {
  [LogisticsOrderStatus.PENDING]: 'warning',
  [LogisticsOrderStatus.IN_TRANSIT]: 'processing',
  [LogisticsOrderStatus.SIGNED]: 'success',
  [LogisticsOrderStatus.CANCELLED]: 'default',
  [LogisticsOrderStatus.EXCEPTION]: 'error',
}

/**
 * 物流轨迹来源标签
 */
export const LOGISTICS_TRACK_SOURCE_LABELS: Record<LogisticsTrackSource, string> = {
  [LogisticsTrackSource.MANUAL]: '手动录入',
  [LogisticsTrackSource.API]: 'API接口',
}

/**
 * 结算单状态标签
 */
export const LOGISTICS_SETTLEMENT_STATUS_LABELS: Record<LogisticsSettlementStatus, string> = {
  [LogisticsSettlementStatus.PENDING_IMPORT]: '待导入',
  [LogisticsSettlementStatus.PENDING_CHECK]: '待核对',
  [LogisticsSettlementStatus.PENDING_CONFIRM]: '待确认',
  [LogisticsSettlementStatus.CONFIRMED]: '已确认',
  [LogisticsSettlementStatus.PAID]: '已付款',
}

/**
 * 结算单状态颜色
 */
export const LOGISTICS_SETTLEMENT_STATUS_COLORS: Record<LogisticsSettlementStatus, string> = {
  [LogisticsSettlementStatus.PENDING_IMPORT]: 'default',
  [LogisticsSettlementStatus.PENDING_CHECK]: 'warning',
  [LogisticsSettlementStatus.PENDING_CONFIRM]: 'processing',
  [LogisticsSettlementStatus.CONFIRMED]: 'success',
  [LogisticsSettlementStatus.PAID]: 'success',
}

/**
 * 结算明细匹配状态标签
 */
export const SETTLEMENT_ITEM_MATCH_STATUS_LABELS: Record<SettlementItemMatchStatus, string> = {
  [SettlementItemMatchStatus.NOT_MATCHED]: '未匹配',
  [SettlementItemMatchStatus.MATCHED]: '已匹配',
  [SettlementItemMatchStatus.DIFFERENCE]: '有差异',
  [SettlementItemMatchStatus.SYSTEM_EXTRA]: '系统多',
  [SettlementItemMatchStatus.IMPORT_EXTRA]: '账单多',
}

/**
 * 结算明细匹配状态颜色
 */
export const SETTLEMENT_ITEM_MATCH_STATUS_COLORS: Record<SettlementItemMatchStatus, string> = {
  [SettlementItemMatchStatus.NOT_MATCHED]: 'default',
  [SettlementItemMatchStatus.MATCHED]: 'success',
  [SettlementItemMatchStatus.DIFFERENCE]: 'warning',
  [SettlementItemMatchStatus.SYSTEM_EXTRA]: 'error',
  [SettlementItemMatchStatus.IMPORT_EXTRA]: 'error',
}

/**
 * 评分等级标签
 */
export const REVIEW_SCORE_LABELS: Record<number, string> = {
  5: '非常满意',
  4: '满意',
  3: '一般',
  2: '不满意',
  1: '非常不满意',
}

